import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { FormProvider, useForm } from 'react-hook-form';

import { useCashAdvanceData } from '@consumer-frontend/nc-api';

import { hasPreviewDepositErrorAtom, requestedDrawAmountAtom } from '../../store';
import { NcMinimumPaymentEstimator } from '../NcMinimumPaymentEstimator';
import {
  NcRequestCashAdvanceCard,
  RequestCashAdvanceFormT,
  useRequestedDrawPreview,
} from '../NcRequestCashAdvanceCard';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/draw-request/request-cash-advance-page.scss';

export const NcRequestCashAdvancePage: React.FC = () => {
  const {
    cashAdvanceData,
    error: cashAdvanceDataError,
    isLoading,
  } = useCashAdvanceData();

  const alreadyRequestedDrawAmount = useAtomValue(requestedDrawAmountAtom);

  const formState = useForm<RequestCashAdvanceFormT>({
    mode: 'onBlur',
    // maxDrawAmount resolves asynchronously; the form syncs once it's available.
    values: { requestedDrawAmount: alreadyRequestedDrawAmount ?? cashAdvanceData?.maximumDrawAmount },
  });

  const previewAmount = useRequestedDrawPreview({
    control: formState.control,
    maxDrawAmount: cashAdvanceData?.maximumDrawAmount,
    minDrawAmount: cashAdvanceData?.minimumDrawAmount,
  });

  const hasPreviewDepositError = useAtomValue(hasPreviewDepositErrorAtom);
  const hasError = !!(cashAdvanceDataError || hasPreviewDepositError);

  // Always scroll to top on mount. Only on initial page load.
  useEffect(
    () => {
      window.scrollTo(0, 0);
    },
    [],
  );

  // Track page view in Datadog RUM. Also only on initial page load.
  useEffect(
    () => { datadogRum.addAction('NcRequestCashAdvancePageView'); },
    [],
  );

  return (
    <div className="nc-request-cash-advance-page">
      <FormProvider {...formState}>
        <NcRequestCashAdvanceCard
          cashAdvanceFee={cashAdvanceData?.drawFeePercentage}
          maxDrawAmount={cashAdvanceData?.maximumDrawAmount}
          minDrawAmount={cashAdvanceData?.minimumDrawAmount}
          isLoading={(isLoading || !cashAdvanceData) && !hasError}
          hasApiError={!isLoading && hasError}
        />
      </FormProvider>

      {!isLoading && !hasError && <NcMinimumPaymentEstimator requestedAmount={previewAmount} />}
    </div>
  );
};
